import { Typography } from 'antd';
import type { CSSProperties, MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import type { TranslationKeys } from '@app/i18n/translationKeys';

const { Text: AntText, Title: AntTitle, Paragraph: AntParagraph } = Typography;

const BASE_BOLD_FONT_WEIGHT = 600;

type TextAlign = 'left' | 'center' | 'right';

type TextType =
  | 'display'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'label1'
  | 'label2'
  | 'caption'
  | 'overline'
  | 'code'
  | 'paragraph';

type TextTypeConfig =
  | {
      component: 'title';
      level: 1 | 2 | 3 | 4 | 5;
      fontSize: number;
      fontWeight: number;
    }
  | {
      component: 'text';
      fontSize: number;
      fontWeight: number;
    }
  | {
      component: 'paragraph';
      fontSize: number;
      fontWeight: number;
    };

type BaseSpacingProps = {
  mt?: number;
  mb?: number;
  mr?: number;
  ml?: number;
};

type TranslationVariables = Record<string, string | number>;

type BaseTranslationProps =
  | {
      transKey: TranslationKeys;
      transVariables?: TranslationVariables;
      untranslatedText?: never;
    }
  | {
      transKey?: never;
      transVariables?: never;
      untranslatedText: string;
    };

type TextProps = BaseSpacingProps &
  BaseTranslationProps & {
    type?: TextType;

    color?: string;
    align?: TextAlign;
    hasUserSelect?: boolean;
    hasTextTransition?: boolean;
    isBold?: boolean;
    fw?: number;
    lh?: number;
    lts?: number | string;
    isTruncated?: boolean;
    lineClamp?: number;
    noWrap?: boolean;

    onClick?: MouseEventHandler<HTMLElement>;
    className?: string;
  };

const TEXT_TYPES: Record<TextType, TextTypeConfig> = {
  display: { component: 'title', level: 1, fontSize: 48, fontWeight: 750 },

  h1: { component: 'title', level: 1, fontSize: 36, fontWeight: 700 },
  h2: { component: 'title', level: 2, fontSize: 30, fontWeight: 650 },
  h3: { component: 'title', level: 3, fontSize: 24, fontWeight: 650 },
  h4: { component: 'title', level: 4, fontSize: 20, fontWeight: 600 },
  h5: { component: 'title', level: 5, fontSize: 18, fontWeight: 600 },
  subtitle1: { component: 'text', fontSize: 18, fontWeight: 500 },
  subtitle2: { component: 'text', fontSize: 16, fontWeight: 500 },
  body1: { component: 'text', fontSize: 16, fontWeight: 400 },
  body2: { component: 'text', fontSize: 14, fontWeight: 400 },
  label1: { component: 'text', fontSize: 14, fontWeight: 600 },
  label2: { component: 'text', fontSize: 12, fontWeight: 600 },
  caption: { component: 'text', fontSize: 12, fontWeight: 400 },
  overline: { component: 'text', fontSize: 12, fontWeight: 600 },
  code: { component: 'text', fontSize: 13, fontWeight: 500 },
  paragraph: { component: 'paragraph', fontSize: 16, fontWeight: 400 },
};

const buildSpacingStyle = ({ mt, mr, mb, ml }: BaseSpacingProps): CSSProperties => ({
  marginTop: mt ?? undefined,
  marginRight: mr ?? undefined,
  marginBottom: mb ?? undefined,
  marginLeft: ml ?? undefined,
});

const buildClampStyle = (lineClamp: number): CSSProperties =>
  lineClamp > 0
    ? {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        WebkitLineClamp: lineClamp,
      }
    : {};

const Text = ({
  type = 'body1',
  color,
  transKey,
  transVariables,
  untranslatedText,
  align = 'left',
  hasUserSelect = true,
  hasTextTransition = true,
  isBold = false,
  fw = 0,
  lh = 1.3,
  lts,
  isTruncated = false,
  lineClamp = 0,
  noWrap = false,
  mt,
  mb,
  mr,
  ml,
  className,
  onClick,
}: TextProps) => {
  const { t } = useTranslation();

  const typeConfig = TEXT_TYPES[type];
  const computedFontWeight = fw || (isBold ? BASE_BOLD_FONT_WEIGHT : typeConfig.fontWeight);

  const textValue = untranslatedText ?? t(transKey, transVariables);

  const inlineStyle: CSSProperties = {
    ...buildSpacingStyle({ mt, mr, mb, ml }),
    ...buildClampStyle(lineClamp),
    color: color || undefined,
    fontSize: typeConfig.fontSize,
    fontWeight: computedFontWeight,
    lineHeight: lh,
    letterSpacing: typeof lts === 'number' ? `${lts}px` : lts,
    textAlign: align,
    userSelect: hasUserSelect ? 'auto' : 'none',
    transition: hasTextTransition ? 'color 150ms ease, opacity 150ms ease' : undefined,
    whiteSpace: noWrap ? 'nowrap' : undefined,
    textTransform: type === 'overline' ? 'uppercase' : undefined,
    // TODO : CHECK this
    fontFamily:
      type === 'code'
        ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
        : undefined,
    ...(type === 'overline' ? { letterSpacing: typeof lts === 'number' ? `${lts}px` : (lts ?? '1px') } : null),
  };

  switch (typeConfig.component) {
    case 'title':
      return (
        <AntTitle level={typeConfig.level} className={className} style={inlineStyle} onClick={onClick}>
          {textValue}
        </AntTitle>
      );

    case 'paragraph': {
      const paragraphEllipsis: false | { rows?: number; tooltip?: string } =
        lineClamp > 0 ? { rows: lineClamp, tooltip: textValue } : isTruncated ? { tooltip: textValue } : false;

      return (
        <AntParagraph className={className} style={inlineStyle} ellipsis={paragraphEllipsis} onClick={onClick}>
          {textValue}
        </AntParagraph>
      );
    }

    default: {
      const textEllipsis: false | { tooltip?: string } = isTruncated || noWrap ? { tooltip: textValue } : false;

      return (
        <AntText className={className} style={inlineStyle} ellipsis={textEllipsis} onClick={onClick}>
          {textValue}
        </AntText>
      );
    }
  }
};

export default Text;
